import { z } from 'zod';

/** Sinal de 5% (Carta de Intenções) ou saldo de 95% (Termo de Aceite). */
export const TIPOS_COBRANCA = ['sinal', 'saldo'] as const;
export const tipoCobrancaSchema = z.enum(TIPOS_COBRANCA);
export type TipoCobranca = z.infer<typeof tipoCobrancaSchema>;

/**
 * Formas de pagamento aceitas pelo Asaas que usamos.
 * `UNDEFINED` deixa a escolha (Pix, cartão ou boleto) com o pagador na própria fatura.
 */
export const FORMAS_PAGAMENTO = ['UNDEFINED', 'PIX', 'CREDIT_CARD', 'BOLETO'] as const;
export const formaPagamentoSchema = z.enum(FORMAS_PAGAMENTO);
export type FormaPagamento = z.infer<typeof formaPagamentoSchema>;

/**
 * Modalidade do link, escolhida pelo **operador** na hora de gerar.
 *
 *  `avista`     — pagamento único. O pagador escolhe entre cartão e Pix.
 *  `parcelado`  — só cartão, e quem escolhe **quantas** parcelas (1 a 12) é o pagador,
 *                 na tela do checkout. O operador libera o parcelamento; não o define.
 *
 * O recorte segue o Termo, cl. 3.2: "cartão de crédito, à vista ou em até doze parcelas,
 * ou Pix à vista". Pix parcelado não existe no instrumento nem no gateway.
 */
export const MODALIDADES_COBRANCA = ['avista', 'parcelado'] as const;
export const modalidadeCobrancaSchema = z.enum(MODALIDADES_COBRANCA);
export type ModalidadeCobranca = z.infer<typeof modalidadeCobrancaSchema>;

/**
 * Situações da cobrança. Espelham os status do Asaas que nos interessam; qualquer
 * outro status recebido pelo webhook é gravado como `OUTRO` e registrado no log.
 */
export const SITUACOES_COBRANCA = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
  'VENCIDA',
  'ESTORNADA',
  'CANCELADA',
  'OUTRO',
] as const;
export const situacaoCobrancaSchema = z.enum(SITUACOES_COBRANCA);
export type SituacaoCobranca = z.infer<typeof situacaoCobrancaSchema>;

export const cobrancaSchema = z.object({
  cobrancaId: z.string(),
  candidatoId: z.string(),
  tipo: tipoCobrancaSchema,
  /**
   * id do checkout no Asaas. É ele que existe no instante em que o link é gerado, e é por
   * ele que o webhook reencontra esta cobrança — a cobrança propriamente dita (`pay_xxx`)
   * só nasce quando o pagador conclui o pagamento.
   */
  asaasCheckoutId: z.string().optional(),
  /**
   * id da cobrança no Asaas (pay_xxx). Ausente enquanto ninguém pagou.
   *
   * Cobranças emitidas antes da migração para o checkout têm este campo preenchido desde a
   * criação e **não** têm `asaasCheckoutId` — daí ele ser opcional dos dois lados.
   */
  asaasId: z.string().optional(),
  /** id do cliente no Asaas (cus_xxx). */
  asaasClienteId: z.string(),
  /** Valor bruto em centavos. */
  valor: z.number().int().nonnegative(),
  /**
   * Parcela que sai da conta titular via split, em centavos. A conta é da RREVELA,
   * então esta é a parte da Academia (70%). Ver packages/shared/src/split.ts.
   */
  valorSplitAcademia: z.number().int().nonnegative(),
  /** O que o link **ofereceu**: `UNDEFINED` (cartão ou Pix) à vista, `CREDIT_CARD` parcelado. */
  formaPagamento: formaPagamentoSchema,
  modalidade: modalidadeCobrancaSchema,
  /** Teto de parcelas oferecido no checkout: 1 à vista, `MAX_PARCELAS_SALDO` parcelado. */
  maxParcelas: z.number().int().positive(),
  /**
   * Parcelas que o pagador de fato escolheu. Só se sabe depois do pagamento, e só se o
   * gateway informar — por isso opcional. Não use para exibir o link antes de pago.
   */
  parcelas: z.number().int().positive().optional(),
  vencimento: z.string(), // AAAA-MM-DD
  situacao: situacaoCobrancaSchema,
  /** URL da fatura para enviar ao pagador. */
  urlFatura: z.string().url(),
  /** Valor efetivamente recebido, em centavos — preenchido pelo webhook. */
  valorRecebido: z.number().int().nonnegative().optional(),
  pagoEm: z.string().datetime().optional(),
  criadoEm: z.string().datetime(),
  criadoPor: z.string(),
  atualizadoEm: z.string().datetime(),
});
export type Cobranca = z.infer<typeof cobrancaSchema>;

/** Corpo aceito por POST /candidatos/:id/cobrancas */
export const criarCobrancaSchema = z
  .object({
    tipo: tipoCobrancaSchema,
    /**
     * Sinal é sempre à vista (Carta, cl. 3.2) — a API rejeita `parcelado` no sinal.
     * No saldo, `parcelado` libera de 1x a `MAX_PARCELAS_SALDO` no cartão, e é o pagador
     * quem escolhe a quantidade (Termo, cl. 3.2).
     */
    modalidade: modalidadeCobrancaSchema.default('avista'),
  })
  .strict();
export type CriarCobranca = z.infer<typeof criarCobrancaSchema>;

export const MAX_PARCELAS_SALDO = 12;

/** Teto de parcelas que o link oferece ao pagador, por modalidade. */
export function maxParcelasDaModalidade(modalidade: ModalidadeCobranca): number {
  return modalidade === 'parcelado' ? MAX_PARCELAS_SALDO : 1;
}

/** O que o checkout oferece ao pagador: parcelado é cartão; à vista, cartão ou Pix. */
export function formaDaModalidade(modalidade: ModalidadeCobranca): FormaPagamento {
  return modalidade === 'parcelado' ? 'CREDIT_CARD' : 'UNDEFINED';
}

/**
 * Situações em que a cobrança ainda "vale": ou está esperando pagamento, ou já foi paga.
 * Existindo uma dessas, emitir outra do mesmo tipo mandaria dois links de seis dígitos
 * para o mesmo pagador.
 */
export const SITUACOES_EM_ABERTO: readonly SituacaoCobranca[] = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
];

export function cobrancaEmAberto(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return SITUACOES_EM_ABERTO.includes(cobranca.situacao);
}

export function cobrancaJaPaga(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return cobranca.situacao === 'CONFIRMADA' || cobranca.situacao === 'RECEBIDA';
}
